/**
 * MIDNIGHT chip family — Chip · ChipGroup · StatusChip
 *
 * @example
 * <ChipGroup>
 *   <Chip active>All</Chip>
 *   <Chip onClick={() => {}}>Leads</Chip>
 *   <Chip tint="mint" leading="●">Paid</Chip>
 *   <Chip tint="peach" onRemove={() => {}}>Quoted: Johnson</Chip>
 * </ChipGroup>
 *
 * <StatusChip status="paid" />             // → Paid pill in mint pastel
 * <StatusChip status="overdue" showDot />  // → Overdue pill in blush with leading dot
 *
 * ## `onPastel` — chip placed on a pastel tile surface
 *
 * The default chip renders with a semi-transparent pastel tint that achieves
 * adequate contrast on a pure-black or dark-glass canvas. However, when the
 * chip is placed *inside* a pastel tile (`.card-mint`, `.ds-mint`, etc.) the
 * same translucent style bleeds into the background and fails contrast.
 *
 * Pass `onPastel={true}` whenever the direct parent surface is a pastel tile.
 * The chip will switch to the corresponding **deep** variant (e.g. mint →
 * `var(--mint-deep)`) with white text, ensuring WCAG AA contrast on bright backgrounds.
 *
 * - `onPastel={false}` (default): existing chip-on-dark-canvas behavior.
 * - `onPastel={true}` + named pastel tint: deep bg, white text, subtle edge border.
 * - `onPastel={true}` + `tint="neutral"`: `rgba(0,0,0,0.4)` bg, white text.
 * - Leading dot color stays the pastel BASE hex so the dot reads as the tint identity.
 */

import React, { KeyboardEvent, useCallback } from 'react'
import { toneForStatus } from '@/lib/phaseTone'

// ─── Token helpers ────────────────────────────────────────────────────────────

/** Canonical pastel tints that map onto the MIDNIGHT palette. */
export type PastelTint =
  | 'mint'
  | 'peach'
  | 'lavender'
  | 'butter'
  | 'blush'
  | 'sky'
  | 'neutral'

interface TintTokens {
  bg: string
  border: string
  color: string
}

/** Deep variants used when `onPastel={true}` — ensures contrast on bright pastel surfaces. */
const TINT_DEEP: Record<PastelTint, { bg: string; dotColor: string }> = {
  neutral:  { bg: 'rgba(0,0,0,0.4)',  dotColor: 'rgb(var(--fg-rgb) / 0.60)' },
  mint:     { bg: 'var(--mint-deep)', dotColor: 'var(--mint)' },
  peach:    { bg: 'var(--peach-deep)', dotColor: 'var(--peach)' },
  lavender: { bg: 'var(--lavender-deep)', dotColor: 'var(--lavender)' },
  butter:   { bg: 'var(--butter-deep)', dotColor: 'var(--butter)' },
  blush:    { bg: 'var(--blush-deep)', dotColor: 'var(--blush)' },
  sky:      { bg: 'var(--sky-deep)', dotColor: 'var(--sky)' },
}

const TINT_STYLES: Record<PastelTint, TintTokens> = {
  neutral: {
    bg: 'var(--tile, rgb(var(--fg-rgb) / 0.04))',
    border: 'var(--border, rgb(var(--fg-rgb) / 0.08))',
    color: 'var(--text2, rgb(var(--fg-rgb) / 0.60))',
  },
  mint: {
    bg: 'rgb(var(--mint-rgb) / 0.14)',
    border: 'rgb(var(--mint-rgb) / 0.28)',
    color: 'var(--mint, var(--mint))',
  },
  peach: {
    bg: 'rgb(var(--peach-rgb) / 0.14)',
    border: 'rgb(var(--peach-rgb) / 0.28)',
    color: 'var(--peach, var(--peach))',
  },
  lavender: {
    bg: 'rgb(var(--lavender-rgb) / 0.14)',
    border: 'rgb(var(--lavender-rgb) / 0.28)',
    color: 'var(--lavender, var(--lavender))',
  },
  butter: {
    bg: 'rgb(var(--butter-rgb) / 0.14)',
    border: 'rgb(var(--butter-rgb) / 0.28)',
    color: 'var(--butter, var(--butter))',
  },
  blush: {
    bg: 'rgb(var(--blush-rgb) / 0.14)',
    border: 'rgb(var(--blush-rgb) / 0.28)',
    color: 'var(--blush, var(--blush))',
  },
  sky: {
    bg: 'rgb(var(--sky-rgb) / 0.14)',
    border: 'rgb(var(--sky-rgb) / 0.28)',
    color: 'var(--sky, var(--sky))',
  },
}

// ─── Canonical status maps ─────────────────────────────────────────────────

/** All lifecycle stages that StatusChip understands. */
export type WorkStatus =
  | 'lead'
  | 'qualified'
  | 'quoted'
  | 'accepted'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'invoiced'
  | 'paid'
  | 'overdue'
  | 'cancelled'
  | 'archived'

// One pastel per phase of work — see lib/phaseTone.
export const STATUS_TINT_MAP: Record<WorkStatus, PastelTint> = {
  lead: toneForStatus('lead'),
  qualified: toneForStatus('qualified'),
  quoted: toneForStatus('quoted'),
  accepted: toneForStatus('accepted'),
  scheduled: toneForStatus('scheduled'),
  in_progress: toneForStatus('in_progress'),
  completed: toneForStatus('completed'),
  invoiced: toneForStatus('invoiced'),
  paid: toneForStatus('paid'),
  overdue: toneForStatus('overdue'),
  cancelled: 'neutral',
  archived: 'neutral',
}

export const STATUS_LABEL_MAP: Record<WorkStatus, string> = {
  lead: 'Lead',
  qualified: 'Qualified',
  quoted: 'Quoted',
  accepted: 'Approved',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  invoiced: 'Invoiced',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  archived: 'Archived',
}

// ─── Chip ─────────────────────────────────────────────────────────────────────

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Pastel tint. Defaults to 'neutral' (glass). */
  tint?: PastelTint
  /** Filter-selected state: white bg, black text. */
  active?: boolean
  /** Height variant. xs = 20px, sm = 24px, md = 30px (default). */
  size?: 'xs' | 'sm' | 'md'
  /** Optional leading node — icon, dot character, Avatar, etc. */
  leading?: React.ReactNode
  /** Optional trailing node.
   * @remarks — If `onRemove` is set, the `trailing` slot is overridden with a close icon.
   */
  trailing?: React.ReactNode
  /** If set, renders a × button and calls this on click. */
  onRemove?: () => void
  /** If set, chip gains role=button, tabIndex=0, keyboard Enter/Space. */
  onClick?: () => void
  /** Unused by this implementation; reserved for future Slot/asChild pattern. */
  asChild?: boolean
  /**
   * Set to `true` when the chip is placed on a pastel tile surface.
   * Switches to a deep-tint background with white text so the chip has
   * adequate contrast against bright pastel backgrounds. Default `false`.
   */
  onPastel?: boolean
}

export function Chip({
  tint = 'neutral',
  active = false,
  size = 'md',
  leading,
  trailing,
  onRemove,
  onClick,
  asChild: _asChild,
  onPastel = false,
  children,
  style,
  ...rest
}: ChipProps): React.ReactElement {
  const tokens = TINT_STYLES[tint]
  const deep = TINT_DEEP[tint]
  const [isPressed, setIsPressed] = React.useState(false)

  // Sizing
  const height = size === 'xs' ? 20 : size === 'sm' ? 24 : 30
  const padding = size === 'xs' ? '0 8px' : size === 'sm' ? '0 10px' : '0 12px'
  const fontSize = size === 'xs' ? 10 : size === 'sm' ? 11 : 13

  // Interactive chip (has onClick or onRemove)
  const isInteractive = typeof onClick === 'function' || typeof onRemove === 'function'

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onClick!()
      }
    },
    [isInteractive, onClick],
  )

  const computedStyle: React.CSSProperties = active
    ? {
        background: 'rgb(var(--fg-rgb))',
        color: 'rgb(var(--bg-rgb))',
        borderColor: 'transparent',
        ...style,
      }
    : onPastel
      ? {
          background: deep.bg,
          color: 'rgb(var(--fg-rgb))',
          border: '0.5px solid rgb(var(--fg-rgb) / 0.22)',
          ...style,
        }
      : {
          background: tokens.bg,
          borderColor: tokens.border,
          color: tokens.color,
          ...style,
        }

  return (
    <span
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      onPointerDown={() => setIsPressed(true)}
      onPointerUp={() => setIsPressed(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        // WCAG 2.5.5: interactive chips must have a 44px minimum tap target
        height: isInteractive ? Math.max(height, 44) : height,
        minHeight: isInteractive ? 44 : undefined,
        padding: isInteractive
          ? (size === 'xs' ? '0 12px' : size === 'sm' ? '0 14px' : '0 16px')
          : padding,
        fontSize,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        borderRadius: 9999,
        border: '0.5px solid',
        cursor: isInteractive ? 'pointer' : 'default',
        userSelect: 'none',
        // Named properties, not `all`: `all` also faded the focus ring in.
        transition: 'transform 0.15s cubic-bezier(0.2, 0.8, 0.2, 1), background-color 0.2s var(--midnight-ease, cubic-bezier(0.2,0.8,0.2,1)), border-color 0.2s var(--midnight-ease, cubic-bezier(0.2,0.8,0.2,1)), color 0.2s var(--midnight-ease, cubic-bezier(0.2,0.8,0.2,1))',
        whiteSpace: 'nowrap',
        transform: isInteractive && isPressed ? 'scale(0.94)' : undefined,
        ...computedStyle,
      }}
      {...rest}
    >
      {leading != null && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 1,
            // When onPastel, override any dot color to use the pastel BASE hex
            ...(onPastel && leading === '●'
              ? { color: deep.dotColor }
              : undefined),
          }}
        >
          {leading}
        </span>
      )}

      {children}

      {trailing != null && !onRemove && (
        <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
          {trailing}
        </span>
      )}

      {typeof onRemove === 'function' && (
        <RemoveButton onRemove={onRemove} label={typeof children === 'string' || typeof children === 'number' ? String(children) : undefined} />
      )}
    </span>
  )
}

// ─── RemoveButton (internal) ──────────────────────────────────────────────────

interface RemoveButtonProps {
  onRemove: () => void
  /** What is removed, so a row of tag chips doesn't read "Remove, Remove". */
  label?: string
}

function RemoveButton({ onRemove, label }: RemoveButtonProps): React.ReactElement {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onRemove()
    },
    [onRemove],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        onRemove()
      }
    },
    [onRemove],
  )

  return (
    <button
      type="button"
      aria-label={label ? `Remove ${label}` : 'Remove'}
      // 16px glyph; 44px hit area on touch.
      className="tap-target"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        padding: 0,
        marginLeft: 2,
        border: 'none',
        borderRadius: 9999,
        background: 'transparent',
        cursor: 'pointer',
        color: 'inherit',
        fontSize: 12,
        lineHeight: 1,
        transition: 'background 0.15s ease',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'rgb(var(--fg-rgb) / 0.12)'
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      ×
    </button>
  )
}

// ─── ChipGroup ────────────────────────────────────────────────────────────────

export interface ChipGroupProps {
  children: React.ReactNode
  /** If false, renders a single horizontal scroll row. Default true (wraps). */
  wrap?: boolean
  /** If true, enables horizontal scroll with no wrapping (takes precedence over `wrap`). */
  scrollable?: boolean
  /** Gap between chips in px. Default 8. */
  gap?: number
  /** aria-label for the wrapping element. */
  ariaLabel?: string
  className?: string
}

export function ChipGroup({
  children,
  wrap = true,
  scrollable = false,
  gap = 8,
  ariaLabel,
  className,
}: ChipGroupProps): React.ReactElement {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={className}
      style={{
        display: 'flex',
        flexWrap: scrollable ? 'nowrap' : wrap ? 'wrap' : 'nowrap',
        gap,
        ...(scrollable
          ? {
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }
          : wrap
            ? {}
            : {
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }),
      }}
    >
      {children}
    </div>
  )
}

// ─── StatusChip ───────────────────────────────────────────────────────────────

export interface StatusChipProps extends Omit<ChipProps, 'tint' | 'leading'> {
  /** Lifecycle status. Drives tint + default label. */
  status: WorkStatus
  /** Whether to prepend a ● dot. Default true. */
  showDot?: boolean
  /** Override the default label derived from STATUS_LABEL_MAP. */
  label?: string
  /**
   * Set to `true` when the chip is placed on a pastel tile surface.
   * Passes through to Chip — see `ChipProps.onPastel` for full docs.
   */
  onPastel?: boolean
}

export function StatusChip({
  status,
  showDot = true,
  label,
  onPastel = false,
  children,
  ...rest
}: StatusChipProps): React.ReactElement {
  const tint = STATUS_TINT_MAP[status] ?? 'neutral'
  const resolvedLabel = label ?? STATUS_LABEL_MAP[status] ?? status
  // When onPastel, dot uses the pastel BASE color; otherwise uses the tint token color
  const dotColor = onPastel ? TINT_DEEP[tint].dotColor : TINT_STYLES[tint].color

  const leading = showDot ? (
    <span
      aria-hidden="true"
      style={{
        color: dotColor,
        fontSize: 12,
        lineHeight: 1,
        display: 'inline-block',
      }}
    >
      ●
    </span>
  ) : undefined

  return (
    <Chip tint={tint} leading={leading} onPastel={onPastel} {...rest}>
      {children ?? resolvedLabel}
    </Chip>
  )
}
